/**
 * anchor-price.js
 *
 * Keeps the Croatian "sidrena cijena" (anchor price, NN 101/2026-1212) correct wherever Debut
 * rewrites a price client-side. Debut has no Section Rendering API, so anything it re-renders
 * in JS loses the metafields the Liquid snippet read.
 *
 * Two independent parts, both no-ops when their markup is absent:
 *
 *   1. Product page / featured product. theme.Product rewrites the price from the ProductJson
 *      blob, which carries no metafields. Listens to Debut's own bubbling 'variantChange' event
 *      and swaps the text from the JSON that snippets/anchor-price-data.liquid emits. Inert
 *      unless a product has more than one variant: with a single variant no data script is
 *      emitted, so the handler returns early.
 *
 *   2. Cart. theme.Cart rebuilds the whole table from a clone of the FIRST row whenever a
 *      quantity changes, so without this every line would inherit the first item's anchor
 *      price. Snapshots what Liquid rendered, keyed by line item key, and restores the right
 *      line on every rebuild.
 *
 * No edits to theme.js are needed or made.
 */
(function() {
  var SELECTOR_ANCHOR = '[data-anchor-price]';
  var SELECTOR_DATA = 'script[data-anchor-price-data]';
  var SELECTOR_PRICE = '[data-price]';
  var CLASS_LABEL = 'anchor-price__label';
  var CLASS_VALUE = 'anchor-price__value';

  /**
   * The markup snippets/anchor-price.liquid would have rendered. Built here only when Liquid
   * had nothing to render at page load but a later state does.
   */
  function buildAnchor(className) {
    var anchor = document.createElement('p');
    anchor.className = className;
    anchor.setAttribute('data-anchor-price', '');

    var label = document.createElement('span');
    label.className = CLASS_LABEL;

    var value = document.createElement('span');
    value.className = CLASS_VALUE;

    anchor.appendChild(label);
    anchor.appendChild(document.createTextNode(' '));
    anchor.appendChild(value);

    return anchor;
  }

  /** textContent, never innerHTML: nothing from the JSON or from a clone can inject markup. */
  function fillAnchor(anchor, entry) {
    var label = anchor.querySelector('.' + CLASS_LABEL);
    var value = anchor.querySelector('.' + CLASS_VALUE);

    if (label) label.textContent = entry.label;
    if (value) value.textContent = entry.value;

    anchor.hidden = false;
  }

  function readPart(anchor, className) {
    var part = anchor.querySelector('.' + className);
    return part ? part.textContent.trim() : '';
  }

  /* ============================ Product page / featured product ============================ */

  function readEntries(container) {
    var el = container.querySelector(SELECTOR_DATA);
    if (!el) return null;

    try {
      var entries = JSON.parse(el.textContent);
      return Array.isArray(entries) ? entries : null;
    } catch (error) {
      return null;
    }
  }

  function findEntry(entries, variantId) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].id === variantId) return entries[i];
    }
    return null;
  }

  /**
   * The first-loaded variant may have had no anchor price, in which case Liquid rendered
   * nothing and there is no node to update. Build one in the same place the snippet would
   * have put it: straight after the price container, inside the theme's price wrapper.
   */
  function createAnchor(container) {
    var price = container.querySelector(SELECTOR_PRICE);
    if (!price || !price.parentNode) return null;

    var anchor = buildAnchor('anchor-price');
    price.parentNode.insertBefore(anchor, price.nextSibling);

    return anchor;
  }

  document.addEventListener('variantChange', function(event) {
    var container = event.target;
    if (!container || typeof container.querySelector !== 'function') return;

    var entries = readEntries(container);
    if (!entries) return;

    var anchor = container.querySelector(SELECTOR_ANCHOR);
    var variant = event.detail && event.detail.variant;
    var entry = variant ? findEntry(entries, variant.id) : null;

    // No anchor price for this variant, or no such variant: show nothing, per the brief.
    if (!entry) {
      if (anchor) anchor.hidden = true;
      return;
    }

    if (!anchor) {
      anchor = createAnchor(container);
      if (!anchor) return;
    }

    fillAnchor(anchor, entry);
  });

  /* ======================================== Cart ========================================== */

  var SELECTOR_CART_ITEMS = '[data-cart-line-items]';
  var SELECTOR_CART_ITEM = '[data-cart-item]';
  var SELECTOR_CART_ITEM_PRICE = '[data-cart-item-price]';
  var ATTR_CART_ITEM_KEY = 'data-cart-item-key';
  var CLASS_CART_ANCHOR = 'anchor-price anchor-price--cart';

  /**
   * What Liquid rendered for each line, keyed by line item key ("<variant id>:<line hash>").
   * The key survives a rebuild (theme.Cart._setLineItemAttributes re-applies it) and is unique
   * per line, so the same variant added twice with different properties stays correct.
   *
   * A quantity change can only lower a quantity or remove a line, never introduce a variant
   * that was not on the page already, so this snapshot stays complete for the life of the page.
   */
  function snapshotCart(list) {
    var snapshot = {};
    var rows = list.querySelectorAll(SELECTOR_CART_ITEM);

    for (var i = 0; i < rows.length; i++) {
      var key = rows[i].getAttribute(ATTR_CART_ITEM_KEY);
      if (!key) continue;

      var anchor = rows[i].querySelector(SELECTOR_ANCHOR);
      snapshot[key] = anchor
        ? { label: readPart(anchor, CLASS_LABEL), value: readPart(anchor, CLASS_VALUE) }
        : null;
    }

    return snapshot;
  }

  /** Same position the snippet uses: straight after the unit price, inside the price cell. */
  function createCartAnchor(row) {
    var price = row.querySelector(SELECTOR_CART_ITEM_PRICE);
    if (!price || !price.parentNode) return null;

    var anchor = buildAnchor(CLASS_CART_ANCHOR);
    price.parentNode.insertBefore(anchor, price.nextSibling);

    return anchor;
  }

  function applyCart(list, snapshot) {
    var rows = list.querySelectorAll(SELECTOR_CART_ITEM);

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var key = row.getAttribute(ATTR_CART_ITEM_KEY);
      var entry = key ? snapshot[key] : null;
      var anchor = row.querySelector(SELECTOR_ANCHOR);

      // This line has no anchor price of its own: never show the cloned row's one.
      if (!entry) {
        if (anchor) anchor.hidden = true;
        continue;
      }

      if (!anchor) {
        anchor = createCartAnchor(row);
        if (!anchor) continue;
      }

      fillAnchor(anchor, entry);
    }
  }

  function watchCart() {
    var list = document.querySelector(SELECTOR_CART_ITEMS);
    if (!list || typeof window.MutationObserver !== 'function') return;

    var snapshot = snapshotCart(list);

    // theme.Cart dispatches no event when it rebuilds, so watch the rows themselves. Only the
    // list's direct children are observed, so the nodes this adds inside a row never re-fire.
    new window.MutationObserver(function() {
      applyCart(list, snapshot);
    }).observe(list, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCart);
  } else {
    watchCart();
  }
})();
